import { Card, LinkButton, Tooltip, Text } from '$components'
import { petShowOptions } from '$api-client/@tanstack/react-query.gen'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { Router } from '$utils/router'
import { ClientRoutePath } from '$features/clients/routes/protected'
import { ClientsFilterParam } from '$features/clients/enums/filter-params'
import { EntityLink } from '$components'
import { EditIcon } from '$components'
import { DateHelper } from '$utils/date'
import { AnimatedOutlet } from '$components'
import { PetRoutePaths } from '$features/pets/routes/protected'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { usePageTitle } from '$components'
import { useSearchParams } from 'react-router-dom'
import { PetsFilterParam } from '$features/pets/enums/filter-params'

export function PetDetailsTab() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { hasSubscriptionExpired } = useTenant()

  const { data } = useQuery({
    ...petShowOptions({
      path: {
        pet: Number(id),
      },
      query: {
        include_deleted: true,
        [PetsFilterParam.IncludeUnapproved]: searchParams.get(PetsFilterParam.IncludeUnapproved) === '1',
      },
    }),
  })

  const pet = data?.data

  // Set page title for pet details tab
  usePageTitle(pet ? `${pet.name} - Details` : 'Pet Details')

  if (!pet) {
    return null
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Parent Information Card */}
      <Card
        rounding="xl"
        padding="none"
      >
        <Card.Header
          padding="sm"
          className="flex justify-between items-center"
        >
          <Text type="h4">
            Parent Information
          </Text>

          {pet.approved_at && (
            <Tooltip
              content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
              disabled={!hasSubscriptionExpired}
            >
              <LinkButton
                variant="secondary"
                size="sm"
                to={Router.buildRoute(
                  PetRoutePaths.PetsParentEdit,
                )}
                icon={EditIcon}
                disabled={hasSubscriptionExpired}
              >
                Edit
              </LinkButton>
            </Tooltip>
          )}
        </Card.Header>

        <Card.Content
          padding="sm"
          className="flex flex-col gap-4"
        >
          <div>
            <Card.Label>Name</Card.Label>
            <EntityLink
              image={pet.parent.image}
              path={Router.getRoute(
                ClientRoutePath.ClientsView,
                { id: pet.parent.id.toString() },
                !pet.parent.approved_at ? { [ClientsFilterParam.IncludeUnapproved]: '1' } : undefined,
              )}
            >
              {`${pet.parent.first_name} ${pet.parent.last_name}`}
            </EntityLink>
          </div>

          <div>
            <Card.Label>Email</Card.Label>
            <Text type="s">
              {pet.parent.email}
            </Text>
          </div>

          <div>
            <Card.Label>Phone</Card.Label>
            <Text type="s">
              {pet.parent.phone ?? 'No phone number'}
            </Text>
          </div>
        </Card.Content>
      </Card>

      {/* Pet Information Card */}
      <Card
        rounding="xl"
        padding="none"
      >
        <Card.Header
          padding="sm"
          className="flex justify-between items-center"
        >
          <Text type="h4">
            Pet Information
          </Text>

          {pet.approved_at && (
            <Tooltip
              content={hasSubscriptionExpired ? SUBSCRIPTION_EXPIRED_MESSAGE : undefined}
              disabled={!hasSubscriptionExpired}
            >
              <LinkButton
                variant="secondary"
                size="sm"
                to={Router.buildRoute(
                  PetRoutePaths.PetsInformationEdit,
                )}
                icon={EditIcon}
                disabled={hasSubscriptionExpired}
              >
                Edit
              </LinkButton>
            </Tooltip>
          )}
        </Card.Header>

        <Card.Content
          padding="sm"
          className="flex flex-col gap-4"
        >
          <div>
            <Card.Label>Breed</Card.Label>
            <Text type="s">{pet.breed}</Text>
          </div>

          <div>
            <Card.Label>Size Category</Card.Label>
            <Text type="s">{pet.size_category?.values?.name ?? '—'}</Text>
          </div>

          <div>
            <Card.Label>Coat Type</Card.Label>
            <Text type="s">{pet.coat_type?.values?.name ?? '—'}</Text>
          </div>

          <div>
            <Card.Label>Gender</Card.Label>
            <Text type="s">
              {pet.gender}
            </Text>
          </div>

          <div>
            <Card.Label>Date of Birth</Card.Label>
            <Text type="s">
              {DateHelper.formatBirthDateWithAge(pet.birth_date)}
            </Text>
          </div>
        </Card.Content>
      </Card>

      <AnimatedOutlet />
    </div>
  )
}
